import { Injectable, inject, resource, computed } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AppSettingsService, type AppSettings } from "@shared/app-settings-service";

@Injectable()
export abstract class HttpService {
    protected readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);

    protected abstract path: string;
    protected abstract urlKey: string;
    protected abstract versionKey: string;

    private urlResource = resource<string, AppSettings | null>({
        params: () => this.appSettingsService.appSettings(),
        loader: async ({ params: appSettings }) => {
            if (!appSettings) return "";

            const url = appSettings[this.urlKey] ?? "";
            const version = appSettings[this.versionKey] ?? "";
            return url + version;
        },
    });
    protected url = computed(() => this.urlResource.value() ?? "");

    protected getUrl(path = this.path): string {
        return this.url() + path;
    }

    protected get<R>(path: string = "", options?: { headers?: HttpHeaders; baseUrl?: string }) {
        options = options ?? {};
        options.headers = options?.headers ?? new HttpHeaders();
        options.headers = options.headers.set("Content-Type", "application/json; charset=utf-8");
        return this.http.get<R>((options.baseUrl ?? this.getUrl()) + path, { headers: options.headers });
    }

    protected put<R>(path: string, body: any, options?: { headers?: HttpHeaders }) {
        options = options ?? {};
        options.headers = options?.headers ?? new HttpHeaders();
        options.headers = options.headers.set("Content-Type", "application/json; charset=utf-8");
        return this.http.put<R>(this.getUrl() + path, body, { headers: options.headers });
    }

    protected patch<R>(path: string, body: any, options?: { headers?: HttpHeaders }) {
        options = options ?? {};
        options.headers = options?.headers ?? new HttpHeaders();
        options.headers = options.headers.set("Content-Type", "application/json; charset=utf-8");
        return this.http.patch<R>(this.getUrl() + path, body, { headers: options.headers });
    }

    protected delete<R>(path: string, options?: { headers?: HttpHeaders }) {
        options = options ?? {};
        options.headers = options?.headers ?? new HttpHeaders();
        options.headers = options.headers.set("Content-Type", "application/json; charset=utf-8");
        return this.http.delete<R>(this.getUrl() + path, { headers: options.headers });
    }

    protected post<R>(path: string, body: any, options?: { headers?: HttpHeaders }) {
        options = options ?? {};
        options.headers = options?.headers ?? new HttpHeaders();
        options.headers = options.headers.set("Content-Type", "application/json; charset=utf-8");
        return this.http.post<R>(this.getUrl() + path, body, { headers: options.headers });
    }
}
